import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, ChevronDown } from "lucide-react";
import api from "../lib/api";
import StockCell from "./admin/StockCell";

const inputCls = "bg-[#191A1F] border border-[#35353B] text-[#F5F3ED] focus:border-[#D4AF37] rounded-sm px-3 py-2 w-full outline-none text-sm";
const btn = "bg-[#D4AF37] text-[#101114] hover:bg-[#F2D98D] disabled:opacity-40 font-bold rounded-sm px-4 py-2 text-sm transition-colors whitespace-nowrap";
const errOf = (e) => e?.response?.data?.detail || "Gagal";

function StockRow({ p, gameName, onSaved }) {
  const [mode, setMode] = useState("adjust");
  const [v, setV] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/products/${p.id}/stock`, { mode, value: Number(v) });
      toast.success(`Stok ${p.name}: ${r.data.stock_available}`);
      setV(""); onSaved();
    } catch (e) { toast.error(errOf(e)); } finally { setBusy(false); }
  };
  return (
    <tr data-testid={`stk-row-${p.id}`} className="border-b border-[#35353B]">
      <td className="py-2 pr-3 text-[#A8A8AD]">{gameName}</td>
      <td className="py-2 pr-3 text-[#F5F3ED]">{p.name}</td>
      <td data-testid={`stk-current-${p.id}`} className="py-2 pr-3 text-right font-bold text-[#D4AF37] whitespace-nowrap"><StockCell product={p} suffix={` ${p.unit}`} onSaved={onSaved} testId={`stk-inline-${p.id}`} /></td>
      <td className="py-2 pr-2">
        <select data-testid={`stk-mode-${p.id}`} value={mode} onChange={(e) => setMode(e.target.value)} className={`${inputCls} !py-1.5 w-32`}>
          <option value="adjust">Tambah / kurang</option>
          <option value="set">Isi langsung</option>
        </select>
      </td>
      <td className="py-2 pr-2"><input data-testid={`stk-input-${p.id}`} type="number" step="any" placeholder={mode === "adjust" ? "+100 / -50" : "1000"} value={v} onChange={(e) => setV(e.target.value)} className={`${inputCls} !py-1.5 w-28`} /></td>
      <td className="py-2"><button data-testid={`stk-save-${p.id}`} onClick={save} disabled={busy || v === "" || (mode === "adjust" && Number(v) === 0)} className="text-xs font-bold text-[#D4AF37] hover:text-[#F2D98D] disabled:opacity-30">{busy ? "..." : "Simpan"}</button></td>
    </tr>
  );
}

function HistoryList({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [openId, setOpenId] = useState(null);
  useEffect(() => { api.get("/admin/stock-history", { params: { limit: 30 } }).then((r) => setRows(r.data)); }, [refreshKey]);
  if (!rows.length) return <p data-testid="stk-history-empty" className="text-xs text-[#A8A8AD]">Belum ada perubahan stok manual.</p>;
  return (
    <div data-testid="stk-history" className="space-y-1 max-h-72 overflow-y-auto">
      {rows.map((h) => (
        <div key={h.id} className="text-xs text-[#A8A8AD] border-b border-[#35353B]/60 py-1.5">
          <span className="text-[#F5F3ED]">{new Date(h.at).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" })}</span> · {h.by} ·{" "}
          <span className={h.type === "bulk" ? "text-[#D4AF37]" : "text-[#5FC3F0]"}>{h.type === "bulk" ? "Massal" : "Manual"}</span> · {h.game_name} · {h.product_name}
          {h.type === "manual" ? <> : {h.old} → <b className="text-[#D4AF37]">{h.new}</b></> : <> ({h.mode === "set" ? `isi ${h.value}` : `${h.value > 0 ? "+" : ""}${h.value}`})
            <button data-testid={`stk-history-expand-${h.id}`} onClick={() => setOpenId(openId === h.id ? null : h.id)} className="ml-1 align-middle"><ChevronDown size={12} className={openId === h.id ? "rotate-180" : ""} /></button></>}
          {openId === h.id && <div className="pl-4 mt-1">{(h.items || []).map((i) => <div key={i.product_id}>{i.product_name}: {i.old} → {i.new}</div>)}</div>}
        </div>
      ))}
    </div>
  );
}

export default function StockManagePanel() {
  const [products, setProducts] = useState([]);
  const [games, setGames] = useState([]);
  const [cats, setCats] = useState([]);
  const [bulk, setBulk] = useState({ game_id: "", category_slug: "", mode: "set", value: "" });
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const load = () => api.get("/products").then((r) => { setProducts(r.data); setTick((x) => x + 1); });
  useEffect(() => {
    load();
    api.get("/games").then((r) => { setGames(r.data); setBulk((b) => ({ ...b, game_id: (r.data.find((g) => /aion\s*2/i.test(g.name)) || r.data[0] || {}).id || "" })); });
    api.get("/categories").then((r) => setCats(r.data));
  }, []);
  const gameName = (id) => games.find((g) => g.id === id)?.name || "-";
  const scope = products.filter((p) => p.game_id === bulk.game_id && (!bulk.category_slug || p.category_slug === bulk.category_slug));
  const applyBulk = async () => {
    const v = Number(bulk.value);
    const desc = bulk.mode === "set" ? `isi stok ${v}` : `${v > 0 ? "tambah" : "kurangi"} ${Math.abs(v)}`;
    if (!window.confirm(`${desc} untuk ${scope.length} produk ${gameName(bulk.game_id)}?`)) return;
    setBusy(true);
    try {
      const r = await api.put("/products-bulk-stock", { ...bulk, value: v, category_slug: bulk.category_slug || null });
      toast.success(`Stok diubah di ${r.data.updated} produk${r.data.skipped ? `, ${r.data.skipped} dilewati (stok tidak cukup)` : ""}`);
      setBulk({ ...bulk, value: "" }); load();
    } catch (e) { toast.error(errOf(e)); } finally { setBusy(false); }
  };
  const term = q.trim().toLowerCase();
  const rows = term ? products.filter((p) => p.name.toLowerCase().includes(term) || gameName(p.game_id).toLowerCase().includes(term)) : [];
  return (
    <div data-testid="stock-manage-panel" className="bg-[#202127] border border-[#35353B] rounded-lg p-6 mb-8">
      <h2 className="text-xl font-bold text-[#F5F3ED] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>Kelola Stok Marketplace</h2>
      <p className="text-xs text-[#A8A8AD] mb-5">Stok di marketplace hanya berubah dari sini atau otomatis saat ada pesanan / pembatalan. Catatan stok supplier tidak menambah stok.</p>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end mb-6 pb-6 border-b border-[#35353B]">
        <select data-testid="stk-bulk-game" className={inputCls} value={bulk.game_id} onChange={(e) => setBulk({ ...bulk, game_id: e.target.value })}>
          {games.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <select data-testid="stk-bulk-category" className={inputCls} value={bulk.category_slug} onChange={(e) => setBulk({ ...bulk, category_slug: e.target.value })}>
          <option value="">Semua kategori</option>
          {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        <select data-testid="stk-bulk-mode" className={inputCls} value={bulk.mode} onChange={(e) => setBulk({ ...bulk, mode: e.target.value })}>
          <option value="set">Isi langsung</option>
          <option value="adjust">Tambah / kurang</option>
        </select>
        <input data-testid="stk-bulk-input" type="number" step="any" placeholder={bulk.mode === "set" ? "1000" : "+100 / -50"} className={inputCls} value={bulk.value} onChange={(e) => setBulk({ ...bulk, value: e.target.value })} />
        <button data-testid="stk-bulk-apply-btn" onClick={applyBulk} disabled={busy || bulk.value === "" || !bulk.game_id} className={btn}>{busy ? "..." : `Terapkan ke ${scope.length} Produk`}</button>
      </div>
      <div className="relative mb-4">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A8AD]" />
        <input data-testid="stk-search-input" placeholder="Cari game atau server, mis. Siel" className={`${inputCls} pl-9`} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      {term && (
        <div className="overflow-x-auto max-h-[420px] overflow-y-auto mb-6">
          <table className="w-full text-sm"><tbody>{rows.slice(0, 60).map((p) => <StockRow key={p.id} p={p} gameName={gameName(p.game_id)} onSaved={load} />)}</tbody></table>
          {rows.length === 0 && <p data-testid="stk-no-results" className="text-xs text-[#A8A8AD] py-3">Tidak ada produk yang cocok.</p>}
        </div>
      )}
      <div className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2">Riwayat Perubahan Stok</div>
      <HistoryList refreshKey={tick} />
    </div>
  );
}
